import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const visaThresholds = {
  Dubai: [
    { type: 'Golden Visa (10 Years)', minAmount: 2000000, benefits: ['10-year renewable residency', 'Family sponsorship', 'No sponsor required', 'Multiple entry visa'] },
    { type: 'Property Investor Visa (2 Years)', minAmount: 750000, benefits: ['2-year renewable residency', 'Family sponsorship', 'Work permit eligible', 'UAE bank account'] },
  ],
  'Abu Dhabi': [
    { type: 'Golden Visa (10 Years)', minAmount: 2000000, benefits: ['10-year renewable residency', 'Family sponsorship', 'No sponsor required'] },
    { type: 'Investor Visa (2 Years)', minAmount: 1000000, benefits: ['2-year renewable residency', 'Family sponsorship', 'Abu Dhabi residency benefits'] },
  ],
  Sharjah: [
    { type: 'Golden Visa (10 Years)', minAmount: 2000000, benefits: ['10-year renewable residency', 'Family sponsorship', 'No sponsor required'] },
    { type: 'Investor Visa (2 Years)', minAmount: 1000000, benefits: ['2-year renewable residency', 'Family sponsorship', 'Sharjah residency'] },
  ],
  Ajman: [
    { type: 'Investor Visa (2 Years)', minAmount: 750000, benefits: ['2-year renewable residency', 'Family sponsorship', 'UAE bank account'] },
  ],
  'Ras Al Khaimah': [
    { type: 'Golden Visa (10 Years)', minAmount: 2000000, benefits: ['10-year renewable residency', 'Family sponsorship', 'No sponsor required'] },
    { type: 'Investor Visa (2 Years)', minAmount: 750000, benefits: ['2-year renewable residency', 'Family sponsorship', 'Work permit eligible'] },
  ],
  Fujairah: [
    { type: 'Investor Visa (2 Years)', minAmount: 750000, benefits: ['2-year renewable residency', 'Family sponsorship'] },
  ],
  'Umm Al Quwain': [
    { type: 'Investor Visa (2 Years)', minAmount: 750000, benefits: ['2-year renewable residency', 'Family sponsorship'] },
  ],
};

const VisaEligibilityChecker = () => {
  const [investmentAmount, setInvestmentAmount] = useState('');
  const [selectedEmirate, setSelectedEmirate] = useState('Dubai');
  const [result, setResult] = useState(null);
  const [inputError, setInputError] = useState('');
  const [isChecking, setIsChecking] = useState(false);

  const checkEligibility = () => {
    const raw = investmentAmount?.replace(/,/g, '')?.trim();
    if (!raw) { setInputError('Please enter an investment amount'); return; }
    const amount = parseFloat(raw);
    if (isNaN(amount)) { setInputError('Please enter a valid number'); return; }
    if (amount <= 0) { setInputError('Investment amount must be greater than 0'); return; }
    if (amount > 500000000) { setInputError('Maximum value is AED 500,000,000'); return; }
    setInputError('');
    setIsChecking(true);
    setResult(null);
    setTimeout(() => {
      const thresholds = visaThresholds?.[selectedEmirate] || visaThresholds?.['Dubai'];
      const sortedThresholds = [...thresholds]?.sort((a, b) => a?.minAmount - b?.minAmount);
      const eligible = sortedThresholds?.filter(t => amount >= t?.minAmount);
      const minThreshold = sortedThresholds?.[0]?.minAmount || 750000;
      setResult({ amount, eligible, emirate: selectedEmirate, minThreshold, thresholds: sortedThresholds });
      setIsChecking(false);
    }, 800);
  };

  const handleAmountChange = (e) => {
    setInvestmentAmount(e?.target?.value);
    if (inputError) setInputError('');
    if (result) setResult(null);
  };

  const handleEmirateChange = (e) => {
    setSelectedEmirate(e?.target?.value);
    setResult(null);
    setInputError('');
  };

  const formatAED = (val) => val?.toLocaleString('en-AE');

  const currentThresholds = visaThresholds?.[selectedEmirate] || visaThresholds?.['Dubai'];
  const sortedCurrentThresholds = [...currentThresholds]?.sort((a, b) => a?.minAmount - b?.minAmount);
  const minInvestorVisa = sortedCurrentThresholds?.[0]?.minAmount || 750000;
  const hasGoldenVisa = sortedCurrentThresholds?.some(t => t?.minAmount >= 2000000);
  const goldenVisaThreshold = sortedCurrentThresholds?.find(t => t?.minAmount >= 2000000)?.minAmount || 2000000;

  return (
    <div className="rounded-2xl p-6 md:p-8" style={{ background: 'rgba(10, 22, 40, 0.9)', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0, 212, 255, 0.1)', border: '1px solid rgba(0, 212, 255, 0.3)' }}>
          <Icon name="CreditCard" size={20} color="#00D4FF" />
        </div>
        <div>
          <h3 className="text-lg font-bold" style={{ color: '#E8F4FF' }}>Visa Eligibility Checker</h3>
          <p className="text-sm" style={{ color: '#6B9EC4' }}>Check which residency visa your investment qualifies for</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: '#6B9EC4' }}>Investment Amount (AED)</label>
          <input
            type="text"
            value={investmentAmount}
            onChange={handleAmountChange}
            placeholder="e.g. 2,000,000"
            className="w-full px-4 py-3 rounded-xl text-sm outline-none"
            style={{ background: 'rgba(0, 212, 255, 0.05)', border: `1px solid ${inputError ? '#FF6464' : 'rgba(0, 212, 255, 0.2)'}`, color: '#E8F4FF' }}
          />
          {inputError && <p className="text-xs mt-1" style={{ color: '#FF6464' }}>{inputError}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: '#6B9EC4' }}>Emirate</label>
          <select
            value={selectedEmirate}
            onChange={handleEmirateChange}
            className="w-full px-4 py-3 rounded-xl text-sm outline-none"
            style={{ background: 'rgba(10, 22, 40, 0.95)', border: '1px solid rgba(0, 212, 255, 0.2)', color: '#E8F4FF' }}
          >
            {['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain']?.map(e => (
              <option key={e} value={e}>{e}</option>
            ))}
          </select>
        </div>
      </div>
      {/* Emirate Visa Tiers Preview */}
      <div className="mb-4 p-3 rounded-xl" style={{ background: 'rgba(0, 212, 255, 0.04)', border: '1px solid rgba(0, 212, 255, 0.12)' }}>
        <div className="text-xs font-semibold mb-2" style={{ color: '#6B9EC4' }}>{selectedEmirate?.toUpperCase()} VISA TIERS</div>
        <div className="flex flex-wrap gap-2">
          {sortedCurrentThresholds?.map((tier, i) => (
            <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded-lg" style={{ background: 'rgba(0, 212, 255, 0.08)', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
              <Icon name="Award" size={12} color="#00D4FF" />
              <span className="text-xs" style={{ color: '#E8F4FF' }}>{tier?.type}</span>
              <span className="text-xs font-bold" style={{ color: '#00D4FF' }}>AED {formatAED(tier?.minAmount)}+</span>
            </div>
          ))}
        </div>
      </div>
      <button
        onClick={checkEligibility}
        disabled={isChecking}
        className="w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2"
        style={{ background: isChecking ? 'rgba(0,212,255,0.3)' : 'linear-gradient(135deg, #00D4FF, #0099CC)', color: '#000', opacity: isChecking ? 0.8 : 1 }}
      >
        {isChecking ? (
          <>
            <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
            <span>Checking Eligibility...</span>
          </>
        ) : (
          'Check Visa Eligibility'
        )}
      </button>

      {/* Loading skeleton while checking */}
      {isChecking && (
        <div className="mt-6 space-y-3 animate-pulse">
          <div className="h-10 rounded-xl" style={{ background: 'rgba(0,212,255,0.08)' }} />
          <div className="h-24 rounded-xl" style={{ background: 'rgba(0,212,255,0.06)' }} />
          <div className="h-24 rounded-xl" style={{ background: 'rgba(0,212,255,0.04)' }} />
        </div>
      )}

      {!isChecking && result && (
        <div className="mt-6 space-y-4">
          <div className="p-3 rounded-xl" style={{ background: 'rgba(0, 212, 255, 0.05)', border: '1px solid rgba(0, 212, 255, 0.15)' }}>
            <span className="text-xs" style={{ color: '#6B9EC4' }}>Checking: </span>
            <span className="text-xs font-bold" style={{ color: '#00D4FF' }}>AED {formatAED(result?.amount)}</span>
            <span className="text-xs" style={{ color: '#6B9EC4' }}> in {result?.emirate}</span>
          </div>
          {result?.eligible?.length > 0 ? (
            result?.eligible?.map((visa, idx) => (
              <div key={idx} className="p-4 rounded-xl" style={{ background: 'rgba(0, 212, 255, 0.08)', border: '1px solid rgba(0, 212, 255, 0.3)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="CheckCircle" size={18} color="#00FF88" />
                  <span className="font-semibold text-sm" style={{ color: '#00FF88' }}>Eligible: {visa?.type}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {visa?.benefits?.map((b, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Icon name="Check" size={14} color="#00D4FF" />
                      <span className="text-xs" style={{ color: '#6B9EC4' }}>{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="p-4 rounded-xl" style={{ background: 'rgba(255, 100, 100, 0.08)', border: '1px solid rgba(255, 100, 100, 0.3)' }}>
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={18} color="#FF6464" />
                <span className="text-sm" style={{ color: '#FF6464' }}>Investment below minimum threshold for {result?.emirate} visa eligibility</span>
              </div>
              <p className="text-xs mt-2" style={{ color: '#6B9EC4' }}>Minimum AED {formatAED(result?.minThreshold)} required for investor visa in {result?.emirate}</p>
              <p className="text-xs mt-1" style={{ color: '#6B9EC4' }}>Your amount: AED {formatAED(result?.amount)} — need AED {formatAED(result?.minThreshold - result?.amount)} more</p>
            </div>
          )}
        </div>
      )}
      {/* Dynamic Summary Cards */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="p-3 rounded-xl text-center" style={{ background: 'rgba(0, 212, 255, 0.05)', border: '1px solid rgba(0, 212, 255, 0.15)' }}>
          <Icon name="Home" size={16} color="#00D4FF" />
          <div className="text-sm font-bold mt-1" style={{ color: '#00D4FF' }}>AED {minInvestorVisa >= 1000000 ? (minInvestorVisa / 1000000)?.toFixed(1) + 'M' : (minInvestorVisa / 1000) + 'K'}</div>
          <div className="text-xs" style={{ color: '#6B9EC4' }}>Min Investor Visa ({selectedEmirate})</div>
        </div>
        <div className="p-3 rounded-xl text-center" style={{ background: 'rgba(0, 212, 255, 0.05)', border: '1px solid rgba(0, 212, 255, 0.15)' }}>
          <Icon name="Star" size={16} color="#00D4FF" />
          <div className="text-sm font-bold mt-1" style={{ color: '#00D4FF' }}>{hasGoldenVisa ? `AED ${goldenVisaThreshold / 1000000}M` : 'N/A'}</div>
          <div className="text-xs" style={{ color: '#6B9EC4' }}>{hasGoldenVisa ? 'Golden Visa Threshold' : 'No Golden Visa'}</div>
        </div>
        <div className="p-3 rounded-xl text-center" style={{ background: 'rgba(0, 212, 255, 0.05)', border: '1px solid rgba(0, 212, 255, 0.15)' }}>
          <Icon name="Clock" size={16} color="#00D4FF" />
          <div className="text-sm font-bold mt-1" style={{ color: '#00D4FF' }}>{hasGoldenVisa ? '2–10 Years' : '2 Years'}</div>
          <div className="text-xs" style={{ color: '#6B9EC4' }}>Visa Duration</div>
        </div>
      </div>
    </div>
  );
};

export default VisaEligibilityChecker;